
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SchoolUserData } from "@/types/auth";
import { handleSignIn, handleSignUp } from "@/lib/auth-operations";

export const useAuthentication = () => {
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // If there's no session and we're not on the auth page, redirect to auth
        if (!session && !window.location.pathname.includes('/auth')) {
          navigate('/auth');
          return;
        }
        
        setInitialized(true);
      } catch (error) {
        console.error('Auth initialization error:', error);
        // On error, clear any existing session and redirect to auth
        await supabase.auth.signOut();
        navigate('/auth');
      }
    };

    // Initialize authentication
    initializeAuth();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        if (!session && !window.location.pathname.includes('/auth')) {
          navigate('/auth');
        }
      } else if (event === 'SIGNED_IN' && session) {
        // Handle successful sign in
        // No need to do anything special here as the session will be automatically managed
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return {
    loading,
    initialized,
    handleSignIn: (email: string, password: string) => 
      handleSignIn(email, password, navigate, toast, setLoading),
    handleSignUp: (email: string, password: string, userData: SchoolUserData = {}) => 
      handleSignUp(email, password, userData, toast, setLoading),
  };
};
